import { useEffect, type ReactNode } from "react";
import { SteuerRueckfrageHost } from "../SteuerRueckfrage";
import { Link } from "react-router";
import { toggleTheme } from "../../lib/theme";
import { Toasts } from "../Toast";
import { CookieEinstellungenKnopf, CookieHinweis } from "../CookieHinweis";
import { WEBSITE_URL } from "../../app/nav";
import { ScrollToTop } from "./ScrollToTop";
import { currentLang, mitSprache, setUiLang, t } from "../../lib/sprache";
import { PLATFORM_NAME } from "../../lib/marke";
import { CreatorOfficeLogo, RahmenKontext } from "./Logo";

/** Rahmen der Website-Seiten (header.webnav, main.web, footer) für Anmelden, Registrieren und Passwort; Kopf und Fuß tragen das CreatorOffice-Logo. */
const site = WEBSITE_URL;

/** title: Name der Seite im Browser-Tab (vor „· CreatorOffice“); Pflicht, damit kein Rahmen nur „CreatorOffice“ zeigt (SICHT-OEFFENTLICH-BETRIEB-33). */
/** schlank: angemeldete Person mitten in einem Ablauf (Passwort nach der Bestätigung): Kopf ohne Website-Wege, Login und Testphase */
export function WebLayout({ children, lang: own, title, schlank }: { children: ReactNode; lang?: "de" | "en"; title: string; schlank?: boolean }) {
  // Seiten mit eigener Sprachwahl (Anmeldung ?sprache=en) geben sie an Kopf und Fuß weiter; Registrieren und Passwort
  // bekommen sie vom SpracheRahmen (?sprache=en → setUiLang). Sprunglink, Hell/Dunkel und die Wege zu Login und
  // Registrierung folgen derselben Sprache (SICHT-OEFFENTLICH-BETRIEB-29).
  if (own) setUiLang(own);
  useDocumentLang(own);
  const lang = own ?? currentLang();
  return (
    <>
      <title>{`${title} · ${PLATFORM_NAME}`}</title>
      <ScrollToTop />
      <a className="skip btn" href="#main">{lang === "en" ? "Skip to content" : "Zum Inhalt"}</a>
      <header className="webnav">
        <a className="brand co-brand" href={`${site}/`}>
          <CreatorOfficeLogo form="responsive" />
        </a>
        {!schlank && <nav aria-label="Website">
          <a href={`${site}/funktionen.html`}>{t("Funktionen", "Features")}</a>
          <a href={`${site}/preise.html`}>{t("Preise", "Pricing")}</a>
          <a className="optional" href={`${site}/sicherheit.html`}>{t("Sicherheit", "Security")}</a>
          <a className="optional" href={`${site}/fuer-creator.html`}>{t("Für Creator", "For creators")}</a>
          <Link className="btn " to={mitSprache("/anmelden", lang)}>Login</Link>
          <Link className="btn primary" to={mitSprache("/registrieren", lang)}>{t("Kostenlos testen", "Try for free")}</Link>
        </nav>}
      </header>
      <RahmenKontext.Provider value={true}>{children}</RahmenKontext.Provider>
      <footer className="footer">
        <a className="brand co-brand" href={`${site}/`}>
          <CreatorOfficeLogo form="full" />
        </a>
        <div className="row">
          <a href={`${site}/ueber-uns.html`}>{t("Über uns", "About us")}</a>
          <a href={`${site}/sicherheit.html`}>{t("Datenschutz & Sicherheit", "Privacy & security")}</a>
          <a href={lang === "en" ? `${site}/en/legal-notice.html` : `${site}/impressum.html`}>{t("Impressum", "Legal notice")}</a>
          <a href={`${site}/datenschutz.html`}>{t("Datenschutz", "Privacy")}</a>
          <a href={`${site}/agb.html`}>{t("AGB", "Terms")}</a>
          <a href={lang === "en" ? `${site}/en/terms-of-use.html` : `${site}/nutzung.html`}>{t("Nutzungsbedingungen", "Terms of use")}</a>
          <CookieEinstellungenKnopf />
          <button className="" onClick={toggleTheme}>{lang === "en" ? "Light / dark" : "Hell / Dunkel"}</button>
        </div>
      </footer>
      <CookieHinweis />
      <Toasts />
      <SteuerRueckfrageHost />
    </>
  );
}

/** Sprache des Dokuments (lang am html) für die Dauer der Seite; Vorleseprogramme sprechen den Text dann richtig aus. */
export function useDocumentLang(lang: "de" | "en" | undefined): void {
  useEffect(() => {
    if (!lang || typeof document === "undefined") return;
    const before = document.documentElement.lang;
    document.documentElement.lang = lang;
    return () => { document.documentElement.lang = before; };
  }, [lang]);
}

/** Absender einer öffentlichen Seite: die Agentur, die den Link verschickt hat (Name im Kopf, Kontakt im Fuß). */
export interface PublicSender {
  name: string;
  /** Kontaktzeilen (Adresse, E-Mail, Website); leere fallen weg */
  contact?: (string | null | undefined)[];
}

/**
 * Rahmen öffentlicher Seiten (Signieren, Rechnung abrufen, Insights, Media-Kit, Einladung, Betreiber): header.webnav + main.auth-shell.
 * Mit lang stehen Sprunglink und Hell/Dunkel in dieser Sprache und das Dokument trägt sie (SICHT-OEFFENTLICH-BETRIEB-05, -13).
 * Der Kopf trägt klein das CreatorOffice-Logo (Regel: der Rahmen gehört der Plattform); die Agentur steht als Absender im
 * Inhalt (Dokumentkopf mit ihrem Logo) und mit ihrem Kontakt im Fuß (SICHT-OEFFENTLICH-BETRIEB-28). Ohne sender (vor der
 * Prüfung der E-Mail-Adresse) bleibt der Fuß weg.
 */
export function PublicLayout({ children, themeLabel, lang, sender }: {
  children: ReactNode; themeLabel?: string; lang?: "de" | "en"; sender?: PublicSender | null;
}) {
  useDocumentLang(lang);
  const en = lang === "en";
  const name = sender?.name.trim() || null;
  const contact = (sender?.contact ?? []).map((c) => c?.trim()).filter(Boolean);
  return (
    <>
      <ScrollToTop />
      <a className="skip btn" href="#main">{en ? "Skip to content" : "Zum Inhalt"}</a>
      <header className="webnav">
        <span className="brand co-brand">
          <CreatorOfficeLogo form="responsive" size="sm" />
        </span>
        <button className="" onClick={toggleTheme}>{themeLabel ?? (en ? "Light / dark" : "Hell / Dunkel")}</button>
      </header>
      <RahmenKontext.Provider value={true}>{children}</RahmenKontext.Provider>
      {name && (
        <footer className="footer tk-noprint tk-public-footer">
          <div className="stack">
            <strong>{name}</strong>
            {contact.length > 0 && <small>{contact.join(" · ")}</small>}
            <small><a href={`${site}/datenschutz.html`} target="_blank" rel="noopener noreferrer">{en ? `Privacy (${PLATFORM_NAME})` : `Datenschutz (${PLATFORM_NAME})`}</a></small>
          </div>
        </footer>
      )}
      <Toasts />
      <SteuerRueckfrageHost />
    </>
  );
}
